import { FlowNodeTypeEnum } from '$/domains/node/type';
import {
  FlowNodeEntity, FreeLayoutPluginContext, FreeOperationType,
} from '@flowgram.ai/free-layout-editor';
import { mtstatTrack } from '$/utils/mtstat';
import useFlowStore from '$/store/useFlowStore';
import { scheduleRegisterWorkflowNodeMediaAsset } from '$/utils/workflowMediaAssetRegister';

const INPUT_NODE_HEIGHT = 160;
const NODE_HORIZONTAL_OFFSET = 150; // 横向 offset
const NODE_VERTICAL_OFFSET = 50; // 纵向 offset

const OFFSET_MAP: Record<string, number> = {
  [FlowNodeTypeEnum.TEXT_NODE]: 40,
  [FlowNodeTypeEnum.IMAGE_NODE]: 180,
  [FlowNodeTypeEnum.VIDEO_NODE]: 180,
  [FlowNodeTypeEnum.AUDIO_NODE]: 120,
};

// 默认动画时长
const DEFAULT_ANIMATION_DURATION = 800;

/**
 * 带动画效果的节点位置更新
 * @param node 节点
 * @param targetPosition 目标位置
 * @param duration 动画时长（毫秒），默认 800ms
 */
export const animateNodePosition = (
  node: FlowNodeEntity,
  targetPosition: { x: number; y: number; },
  duration: number = DEFAULT_ANIMATION_DURATION,
) => {
  const startPosition = { ...node.transform.position };
  const startTime = performance.now();

  const animate = (currentTime: number) => {
    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);

    // 使用 easeOutCubic 缓动函数
    const easeProgress = 1 - (1 - progress) ** 3;

    const currentPosition = {
      x: startPosition.x + (targetPosition.x - startPosition.x) * easeProgress,
      y: startPosition.y + (targetPosition.y - startPosition.y) * easeProgress,
    };

    node.transform.transform.update({
      position: currentPosition,
    });

    if (progress < 1) {
      requestAnimationFrame(animate);
    }
  };

  requestAnimationFrame(animate);
};

export const calcPostionInChatFlow = (nodeType: string | null) => {
  if (!nodeType) {
    return { INPUT_NODE_OFFSET: 0, offset: 20 };
  }
  // let random = Math.random();
  // while (Math.abs(random - randomLru[randomLru.length - 1]) < 0.1) {
  //   random = Math.random();
  // }
  const INPUT_NODE_OFFSET = OFFSET_MAP[nodeType!];
  const offset = INPUT_NODE_HEIGHT + INPUT_NODE_OFFSET;
  return { INPUT_NODE_OFFSET, offset };
};

export const getClusterNodes = (nodes: FlowNodeEntity[], distanceThreshold: number = 400): FlowNodeEntity[][] => {
  if (!nodes || nodes.length === 0) return [];

  const visited = new Set<string>();
  const clusters: FlowNodeEntity[][] = [];

  let s = 0;

  const calculateDistance = (node1: FlowNodeEntity, node2: FlowNodeEntity): number => {
    const pos1 = node1.transform.position;
    const pos2 = node2.transform.position;
    s += 1;
    console.log(`Distance calculation ${s}:`, Math.sqrt((pos1.x - pos2.x) ** 2 + (pos1.y - pos2.y) ** 2));
    return Math.sqrt((pos1.x - pos2.x) ** 2 + (pos1.y - pos2.y) ** 2);
  };

  const nodeMap = new Map<string, FlowNodeEntity>();
  nodes.forEach((node) => {
    nodeMap.set(node.id, node);
  });

  const adjacencyMap = new Map<string, Set<string>>();
  for (let i = 0; i < nodes.length; i += 1) {
    const node1 = nodes[i];
    if (!adjacencyMap.has(node1.id)) {
      adjacencyMap.set(node1.id, new Set());
    }

    for (let j = i + 1; j < nodes.length; j += 1) {
      const node2 = nodes[j];
      const distance = calculateDistance(node1, node2);

      if (distance <= distanceThreshold) {
        adjacencyMap.get(node1.id)!.add(node2.id);
        if (!adjacencyMap.has(node2.id)) {
          adjacencyMap.set(node2.id, new Set());
        }
        adjacencyMap.get(node2.id)!.add(node1.id);
      }
    }
  }

  const dfs = (nodeId: string, cluster: FlowNodeEntity[]) => {
    if (visited.has(nodeId)) return;

    visited.add(nodeId);
    const node = nodeMap.get(nodeId);
    if (node) {
      cluster.push(node);
    }

    const neighbors = adjacencyMap.get(nodeId);
    if (neighbors) {
      neighbors.forEach((neighborId) => {
        if (!visited.has(neighborId)) {
          dfs(neighborId, cluster);
        }
      });
    }
  };

  nodes.forEach((node) => {
    if (!visited.has(node.id)) {
      const cluster: FlowNodeEntity[] = [];
      dfs(node.id, cluster);
      if (cluster.length > 0) {
        clusters.push(cluster);
      }
    }
  });
  const _clusters = clusters.sort((a, b) => b.length - a.length);
  return _clusters;
};

export const getClusterBounds = (clusters: FlowNodeEntity[][]): Array<{
  x: number;
  y: number;
  width: number;
  height: number;
  top: number;
  left: number;
  right: number;
  bottom: number;
}> => {
  return clusters.map((cluster) => {
    if (cluster.length === 0) {
      return {
        x: 0,
        y: 0,
        width: 0,
        height: 0,
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
      };
    }

    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;

    cluster.forEach((node) => {
      const { position } = node.transform;
      const { bounds } = node.transform;

      const nodeLeft = position.x;
      const nodeTop = position.y;
      const nodeRight = position.x + bounds.width;
      const nodeBottom = position.y + bounds.height;

      minX = Math.min(minX, nodeLeft);
      minY = Math.min(minY, nodeTop);
      maxX = Math.max(maxX, nodeRight);
      maxY = Math.max(maxY, nodeBottom);
    });

    return {
      x: minX,
      y: minY,
      width: maxX - minX,
      height: maxY - minY,
      top: minY,
      left: minX,
      right: maxX,
      bottom: maxY,
    };
  });
};

export const getAllNodesBounds = (nodes: FlowNodeEntity[]): {
  x: number;
  y: number;
  width: number;
  height: number;
  top: number;
  left: number;
  right: number;
  bottom: number;
} => {
  if (nodes.length === 0) {
    return {
      x: 0,
      y: 0,
      width: 0,
      height: 0,
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
    };
  }

  let minX = Infinity;
  let minY = Infinity;
  let maxX = -Infinity;
  let maxY = -Infinity;

  nodes.forEach((node) => {
    const { position } = node.transform;
    const { bounds } = node.transform;

    const nodeLeft = position.x;
    const nodeTop = position.y;
    const nodeRight = position.x + bounds.width;
    const nodeBottom = position.y + bounds.height;

    minX = Math.min(minX, nodeLeft);
    minY = Math.min(minY, nodeTop);
    maxX = Math.max(maxX, nodeRight);
    maxY = Math.max(maxY, nodeBottom);
  });

  return {
    x: minX,
    y: minY,
    width: maxX - minX,
    height: maxY - minY,
    top: minY,
    left: minX,
    right: maxX,
    bottom: maxY,
  };
};

export const nodeAddedPosition = (
  ctx: FreeLayoutPluginContext,
  editNodeListToAdd: any[],
  textNodeListToAdd: any[],
  taskId?: string,
) => {
  const nodes = ctx.document.getAllNodes();
  const allNodesBounds = getAllNodesBounds(nodes);
  let meta = {
    position: {
      x: allNodesBounds.left,
      y: allNodesBounds.bottom,
    },
  };
  let hasText = false;
  if (textNodeListToAdd?.length) {
    hasText = true;
    textNodeListToAdd.forEach((node, index) => {
      const lastType = index ? FlowNodeTypeEnum.TEXT_NODE : null;
      const { offset } = calcPostionInChatFlow(lastType);
      meta = {
        position: {
          x: allNodesBounds.left,
          y: meta.position.y + offset,
        },
      };
      ctx.document.createWorkflowNode({ ...node, meta });
    });
  }

  if (editNodeListToAdd?.length) {
    editNodeListToAdd.forEach((node) => {
      meta = {
        position: {
          x: hasText ? allNodesBounds.left + NODE_HORIZONTAL_OFFSET * 3 : allNodesBounds.left,
          y: meta.position.y + NODE_VERTICAL_OFFSET,
        },
      };
      ctx.document.createWorkflowNode({ ...node, meta });
    });
    // const lastNodeId = editNodeListToAdd[editNodeListToAdd.length - 1].id;
    // const lastNode = ctx.document.getNode(lastNodeId);
    const contentTypeMap: Record<string, string> = {
      [FlowNodeTypeEnum.TEXT_NODE]: 'text',
      [FlowNodeTypeEnum.TEXT_EDIT_NODE]: 'text',
      [FlowNodeTypeEnum.IMAGE_NODE]: 'image',
      [FlowNodeTypeEnum.IMAGE_EDIT_NODE]: 'image',
      [FlowNodeTypeEnum.VIDEO_NODE]: 'video',
      [FlowNodeTypeEnum.VIDEO_EDIT_NODE]: 'video',
      [FlowNodeTypeEnum.AUDIO_NODE]: 'audio',
      [FlowNodeTypeEnum.AUDIO_EDIT_NODE]: 'audio',
    };
    textNodeListToAdd.forEach((item) => {
      // 埋点：工作流创建输入节点
      mtstatTrack('workflow_input_create', {
        source: 'agent_chat',
        room_id: useFlowStore.getState().roomId,
        content_type: contentTypeMap[item.type] || '',
        node_id: item.id,
        model_name: item.data?.apiName || '',
        source_task_id: taskId || '',
      });
    });
    editNodeListToAdd.forEach((item) => {
      // 埋点：工作流创建编辑节点
      mtstatTrack('workflow_edit_create', {
        source: 'agent_chat',
        room_id: useFlowStore.getState().roomId,
        content_type: contentTypeMap[item.type] || '',
        node_id: item.id,
        model_name: item.data?.apiName || '',
        source_task_id: taskId || '',
      });
    });
    const allNodesData = [...editNodeListToAdd, ...textNodeListToAdd];
    const allNodes = allNodesData
      .map((node) => ctx.document.getNode(node.id))
      .filter((node): node is FlowNodeEntity => !!node);
    if (allNodes.length) {
      ctx.playground.scrollToView({
        entities: allNodes, zoom: ctx.playground.config.zoom, easingDuration: 800, scrollToCenter: true,
      });
    }
  }

  const createdNodeIds = [
    ...(textNodeListToAdd || []).map((n) => n.id),
    ...(editNodeListToAdd || []).map((n) => n.id),
  ];
  [...new Set(createdNodeIds)].forEach((id) => {
    const entity = ctx.document.getNode(id);
    if (entity) {
      scheduleRegisterWorkflowNodeMediaAsset(entity, { sourceTaskId: taskId, ctx });
    }
  });
};

// export const scrollToViewConfig = {
//   easing: 'ease-out',
//   duration: 300,
// };

/**
 * 方向指令类型
 * t: 上, b: 下, l: 左, r: 右
 * 拼接规则: tb在前, lr在后，如 'tl', 'br', 't', 'r' 等
 */
export type DirectionCommand = 't' | 'b' | 'l' | 'r' | 'tl' | 'tr' | 'bl' | 'br';

/**
 * 检查两个 bounding 是否重叠
 */
const isBoundingOverlapping = (
  bounds1: { left: number; top: number; right: number; bottom: number; },
  bounds2: { left: number; top: number; right: number; bottom: number; },
  padding: number = 0,
): boolean => {
  return !(
    bounds1.right + padding < bounds2.left - padding
    || bounds1.left - padding > bounds2.right + padding
    || bounds1.bottom + padding < bounds2.top - padding
    || bounds1.top - padding > bounds2.bottom + padding
  );
};

/**
 * 根据节点的 bounding 和新位置，计算新位置下的 bounding
 * position 的 x,y 是 topCenter（顶部中心点）
 */
const getNodeBoundsAtPosition = (
  node: FlowNodeEntity,
  position: { x: number; y: number; },
): { left: number; top: number; right: number; bottom: number; } => {
  const { bounds } = node.transform;

  // 计算节点的宽度和高度
  const width = bounds.right - bounds.left;
  const height = bounds.bottom - bounds.top;

  // position 是 topCenter，所以 left = x - width/2, top = y
  return {
    left: position.x - width / 2,
    top: position.y,
    right: position.x + width / 2,
    bottom: position.y + height,
  };
};

/**
 * 检查节点在新位置是否与其他节点重叠（基于 bounding）
 */
const isNodeBoundsOverlappingAnyNode = (
  node: FlowNodeEntity,
  position: { x: number; y: number; },
  nodes: FlowNodeEntity[],
  padding: number = 0,
): boolean => {
  const newBounds = getNodeBoundsAtPosition(node, position);
  return nodes.some((otherNode) => isBoundingOverlapping(newBounds, otherNode.transform.bounds, padding));
};

/**
 * 根据方向指令计算偏移量
 * @param direction 方向指令，如 't', 'b', 'l', 'r', 'tl', 'tr', 'bl', 'br'
 * @returns 偏移量 { x, y }
 */
const getOffsetByDirection = (direction: DirectionCommand): { x: number; y: number; } => {
  let offsetX = 0;
  let offsetY = 0;

  // 处理垂直方向 (t/b 在前)
  if (direction.includes('t')) {
    offsetY = -(INPUT_NODE_HEIGHT + NODE_VERTICAL_OFFSET);
  } else if (direction.includes('b')) {
    offsetY = INPUT_NODE_HEIGHT + NODE_VERTICAL_OFFSET;
  }

  // 处理水平方向 (l/r 在后)
  if (direction.includes('l')) {
    offsetX = -NODE_HORIZONTAL_OFFSET;
  } else if (direction.includes('r')) {
    offsetX = NODE_HORIZONTAL_OFFSET;
  }

  return { x: offsetX, y: offsetY };
};

/**
 * 根据方向指令计算不重叠的节点位置
 * @param ctx flowgram 上下文
 * @param position 初始位置
 * @param direction 方向指令，如 't', 'b', 'l', 'r', 'tl', 'tr', 'bl', 'br'
 * @param maxIterations 最大迭代次数，防止无限循环
 * @returns 计算后的位置
 */
export const calculateNonOverlappingPosition = (
  ctx: FreeLayoutPluginContext,
  needMoveNode: FlowNodeEntity,
  position: { x: number; y: number; },
  direction: DirectionCommand,
  maxIterations: number = 100,
): { x: number; y: number; } => {
  const nodes = ctx.document.getAllNodes().filter((node) => node.id !== needMoveNode.id);
  if (nodes.length === 0) {
    return position;
  }

  const offset = getOffsetByDirection(direction);
  let currentPosition = { ...position };
  let iterations = 0;

  // 持续检查节点在新位置的 bounding 是否与其他节点重叠，如果重叠则按方向偏移
  while (isNodeBoundsOverlappingAnyNode(needMoveNode, currentPosition, nodes) && iterations < maxIterations) {
    currentPosition = {
      x: currentPosition.x + offset.x,
      y: currentPosition.y + offset.y,
    };
    iterations += 1;
  }

  if (iterations >= maxIterations) {
    console.warn('[calculateNonOverlappingPosition] Max iterations reached, position may still overlap');
  }

  return currentPosition;
};

/**
 * 连线后调整节点位置
 * 1. 如果 targetNodeId 在 sseAddedNodeIds 中，将 targetNode 移动到 sourceNode 后面
 * 2. 如果 targetNode 有 text 节点连接且该 text 节点也在 sseAddedNodeIds 中，将 text 节点移动到 targetNode 左上方
 */
export const adjustNodePositionAfterEdge = (
  ctx: FreeLayoutPluginContext,
  sourceNodeId: string,
  targetNodeId: string,
  sseAddedNodeIds: string[],
) => {
  const operationPreList = [];
  // 如果 targetNodeId 不在 sseAddedNodeIds 中，不需要调整位置
  if (!sseAddedNodeIds.includes(targetNodeId)) {
    return;
  }

  const sourceNode = ctx.document.getNode(sourceNodeId);
  const targetNode = ctx.document.getNode(targetNodeId);

  const needScrollList = [];

  if (!sourceNode || !targetNode) {
    return;
  }

  // 获取 sourceNode 的位置
  const sourcePosition = sourceNode.transform.position;

  // 将 targetNode 移动到 sourceNode 的右侧
  const newTargetPosition = {
    x: sourcePosition.x + NODE_HORIZONTAL_OFFSET * 3,
    y: sourcePosition.y,
  };
  const _newTargetPosition = calculateNonOverlappingPosition(ctx, targetNode, newTargetPosition, 'r');

  // 使用动画效果移动 targetNode
  animateNodePosition(targetNode, _newTargetPosition);
  needScrollList.push(targetNode);

  operationPreList.push({
    id: targetNodeId,
    oldValue: targetNode.transform.position,
    newValue: _newTargetPosition,
  });

  console.log('[adjustNodePositionAfterEdge] Moved targetNode:', targetNodeId, 'to position:', _newTargetPosition);

  // 检查 targetNode 是否有 text 节点连接
  const nodeLines = targetNode.lines;
  const inputLines = (nodeLines as any)?.inputLines || [];

  // 找到连接到 targetNode 的 text 节点
  inputLines.forEach((line: any) => {
    const fromNode = line.fromPort?.node;
    if (!fromNode) return;

    const fromNodeJson = fromNode.toJSON();
    const fromNodeId = fromNode.id;

    // 检查是否是 TEXT_NODE 类型且在 sseAddedNodeIds 中
    if (fromNodeJson?.type === FlowNodeTypeEnum.TEXT_NODE && sseAddedNodeIds.includes(fromNodeId)) {
      // 将 text 节点移动到 targetNode 的上方
      const textNodePosition = {
        x: _newTargetPosition.x,
        y: _newTargetPosition.y - INPUT_NODE_HEIGHT - NODE_VERTICAL_OFFSET,
      };

      const _textNodePosition = calculateNonOverlappingPosition(ctx, fromNode, textNodePosition, 'l');

      // 使用动画效果移动 text 节点
      animateNodePosition(fromNode, _textNodePosition);
      operationPreList.push({
        id: fromNodeId,
        oldValue: fromNode.transform.position,
        newValue: _textNodePosition,
      });

      console.log('[adjustNodePositionAfterEdge] Moved textNode:', fromNodeId, 'to position:', _textNodePosition);
    }
    needScrollList.push(fromNode);
  });

  ctx.history.pushOperation({
    type: FreeOperationType.dragNodes,
    value: {
      ids: [...operationPreList.map((item) => item.id)],
      value: [...operationPreList.map((item) => item.newValue)],
      oldValue: [...operationPreList.map((item) => item.oldValue)],
    },
  });

  ctx.playground.scrollToView({
    entities: needScrollList,
    zoom: ctx.playground.config.zoom > 0.5 ? ctx.playground.config.zoom * 0.8 : ctx.playground.config.zoom,
    easingDuration: 800,
    scrollToCenter: true,
  });
};
